import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BackButton, TableAccordionInfinite, TableAccordionColumn } from '@marageti/z4-lib';
import AuditFilters from '../audit-filters';
import {
  SalesStageAudit,
  SalesStage,
  SalesStageAction,
} from '../types';
import { SALES_STAGES, SALES_STAGE_ACTIONS } from '../constants';
import { formatDateSafe } from '../audit-utils';

const getStageLabel = (value: string) => {
  const stage = SALES_STAGES.find((s) => s.value === value);
  return stage?.label || value;
};

const getActionLabel = (value: string) => {
  const action = SALES_STAGE_ACTIONS.find((a) => a.value === value);
  return action?.label || value;
};

const getIconButton = (path: string, value: unknown, tooltip?: string | null) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <span>{String(value ?? '')}</span>

    <Tooltip title={tooltip} disableHoverListener={!tooltip}>
      <IconButton
        component={Link}
        to={path}
        size="small"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{ p: 0.5 }}
        target="_blank"
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

const columns: TableAccordionColumn<SalesStageAudit>[] = [
  {
    fieldPath: 'tripMatchId',
    label: 'Trip Match ID',
    format: (value) => getIconButton(`/trip-matches/${value}`, value, 'Go to Trip Match'),
  },
  {
    fieldPath: 'stage',
    label: 'Stage',
    format: (value) => getStageLabel(String(value)),
  },
  {
    fieldPath: 'overdue',
    label: 'Overdue',
    style: { width: '80px' },
    format: (value) => (
      <Chip
        label={value ? 'Yes' : 'No'}
        color={value ? 'error' : 'success'}
        size="small"
      />
    ),
  },
  {
    fieldPath: 'actionTaken',
    label: 'Action',
    format: (value) => getActionLabel(String(value)),
  },
  {
    fieldPath: 'actionTakenAt',
    label: 'Action At',
    style: { width: '202px', maxWidth: '202px', minWidth: '202px' },
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'startAt',
    label: 'Start At',
    style: { width: '202px', maxWidth: '202px', minWidth: '202px' },
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'dueAt',
    label: 'Due At',
    style: { width: '202px', maxWidth: '202px', minWidth: '202px' },
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'origDueAt',
    label: 'Original Due At',
    isInAccordion: true,
    format: (value) => formatDateSafe(value),
  },
  {
    fieldPath: 'partnerId',
    label: 'Partner ID',
    isInAccordion: true,
    format: (value) => getIconButton(`/partners/${value}`, value, 'Go to Partner'),
  },
  {
    fieldPath: 'assignedAgentId',
    label: 'Agent ID',
    isInAccordion: true,
    format: (value) => getIconButton(`/agents/${value}`, value, 'Go to Agent'),
  },
  {
    fieldPath: 'index',
    label: 'Index',
    isInAccordion: true,
  },
  {
    fieldPath: 'comments',
    label: 'Comments',
    isInAccordion: true,
  },
];

interface SalesStageViewFilters {
  tripMatchId: string;
  partnerId: string;
  assignedAgentId: string;
  stage: string;
  overdue: string;
  actionTaken: string;
  startDate: string;
  dueAt: string;
}

interface SalesStageViewProps {
  audits: SalesStageAudit[];
  isLoading: boolean;
  error: { message: string } | undefined;
  hasMore: boolean;
  totalHits: number;
  loadMore: () => void;
  filters: SalesStageViewFilters;
  onFilterChange: (name: string, value: unknown) => void;
}

const SalesStageView: React.FC<SalesStageViewProps> = ({
  audits,
  isLoading,
  error,
  hasMore,
  totalHits,
  loadMore,
  filters,
  onFilterChange,
}) => {
  const navigate = useNavigate();

  const filterFields = [
    {
      name: 'tripMatchId',
      label: 'Trip Match ID',
      type: 'text' as const,
      value: filters.tripMatchId || '',
      onChange: (value: unknown) => onFilterChange('tripMatchId', value),
    },
    {
      name: 'partnerId',
      label: 'Partner ID',
      type: 'text' as const,
      value: filters.partnerId || '',
      onChange: (value: unknown) => onFilterChange('partnerId', value),
    },
    {
      name: 'assignedAgentId',
      label: 'Agent ID',
      type: 'text' as const,
      value: filters.assignedAgentId || '',
      onChange: (value: unknown) => onFilterChange('assignedAgentId', value),
    },
    {
      name: 'stage',
      label: 'Stage',
      type: 'select' as const,
      options: SALES_STAGES.map((s) => ({ label: s.label, value: s.value })),
      value: filters.stage || '',
      onChange: (value: unknown) => onFilterChange('stage', value as SalesStage | ''),
    },
    {
      name: 'overdue',
      label: 'Overdue',
      type: 'select' as const,
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      value: filters.overdue || '',
      onChange: (value: unknown) => onFilterChange('overdue', value),
    },
    {
      name: 'actionTaken',
      label: 'Action',
      type: 'select' as const,
      options: SALES_STAGE_ACTIONS.map((a) => ({ label: a.label, value: a.value })),
      value: filters.actionTaken || '',
      onChange: (value: unknown) => onFilterChange('actionTaken', value as SalesStageAction | ''),
    },
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date' as const,
      value: filters.startDate || '',
      onChange: (value: unknown) => onFilterChange('startDate', value),
    },
    {
      name: 'dueAt',
      label: 'Due At',
      type: 'date' as const,
      value: filters.dueAt || '',
      onChange: (value: unknown) => onFilterChange('dueAt', value),
    },
  ];

  return (
    <Box>
      <Stack alignItems="center" justifyContent="flex-start" flexDirection="row" gap={1} sx={{ mb: 2 }}>
        <BackButton onClick={() => navigate('/settings/audit')} />
        <Typography variant="h5">
          Sales Stage Audits
        </Typography>
      </Stack>

      <AuditFilters
        fields={filterFields}
        error={error as Error | null}
        total={`${audits.length} of ${totalHits} records loaded`}
      />

      <TableAccordionInfinite<SalesStageAudit>
        columns={columns}
        data={audits}
        getRowId={(audit) => audit.id}
        isLoading={isLoading}
        hasMore={hasMore}
        loadMore={loadMore}
        emptyMessage="No sales stage audit records found."
        isEmpty={audits.length === 0}
      />
    </Box>
  );
};

export default SalesStageView;
